import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import {
  adminCreateCompetition,
  adminUpdateCompetition,
  adminDeleteCompetition,
  adminSearchSportsDbLeagues,
  adminSyncCompetition
} from '../services/competitionApi';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Flag, RefreshCw, ClipboardCheck } from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DateInputDMY } from '../components/ui/DateInputDMY';
import {
  getCompetitionAltName,
  getCompetitionDisplayName
} from '../utils/competitionDisplayName';

function toInputDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jerusalem',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  if (y && m && day) return `${y}-${m}-${day}`;
  return d.toISOString().slice(0, 10);
}

const emptyForm = () => ({
  nameHe: '',
  nameEn: '',
  slug: '',
  type: 'league',
  startDate: '',
  endDate: '',
  isActive: true,
  isDefault: false,
  bettingLocked: false,
  externalLeagueId: '',
  externalSeason: '',
  syncEnabled: true,
  leagueSearch: '',
  selectedLeagueLabel: '',
  seasonOptions: []
});

const SLUG_OK = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const AdminCompetitions = () => {
  const { t, language, isRTL } = useLanguage();
  const { competitions, refreshCompetitions, setCompetition } = useCompetition();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [leagueOptions, setLeagueOptions] = useState([]);
  const [leagueSearching, setLeagueSearching] = useState(false);
  const searchTimer = useRef(null);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setLeagueOptions([]);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditingId(c._id);
    setForm({
      nameHe: c.nameHe ?? c.name ?? '',
      nameEn: c.nameEn ?? '',
      slug: c.slug || '',
      type: c.type === 'other' ? 'other' : 'league',
      startDate: toInputDate(c.startDate),
      endDate: toInputDate(c.endDate),
      isActive: Boolean(c.isActive),
      isDefault: Boolean(c.isDefault),
      bettingLocked: Boolean(c.bettingLocked),
      externalLeagueId: c.externalLeagueId != null ? String(c.externalLeagueId) : '',
      externalSeason: c.externalSeason != null ? String(c.externalSeason) : '',
      syncEnabled: c.syncEnabled !== false,
      leagueSearch: '',
      selectedLeagueLabel:
        c.externalLeagueId != null
          ? `League #${c.externalLeagueId}${c.externalSeason != null ? ` / ${c.externalSeason}` : ''}`
          : '',
      seasonOptions: c.externalSeason ? [String(c.externalSeason)] : []
    });
    setLeagueOptions([]);
    setShowModal(true);
  };

  const errToast = useCallback(
    (err) => {
      const msg = err?.response?.data?.message;
      const arr = err?.response?.data?.errors;
      if (Array.isArray(arr) && arr.length) {
        toast.error(arr.map((e) => e.msg || e.message).join('; '));
      } else if (msg) {
        toast.error(msg);
      } else {
        toast.error(language === 'en' ? 'Request failed' : 'הבקשה נכשלה');
      }
    },
    [language]
  );

  useEffect(() => {
    const q = form.leagueSearch.trim();
    if (q.length < 2) {
      setLeagueOptions([]);
      return undefined;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        setLeagueSearching(true);
        const data = await adminSearchSportsDbLeagues(q);
        setLeagueOptions(Array.isArray(data?.leagues) ? data.leagues : []);
      } catch (err) {
        setLeagueOptions([]);
        if (err?.response?.status !== 503) {
          // soft fail while typing
        }
      } finally {
        setLeagueSearching(false);
      }
    }, 400);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [form.leagueSearch]);

  const selectLeague = (league) => {
    const current =
      (league.seasons || []).find((s) => s.current) || (league.seasons || [])[0];
    const seasonYear = current?.year != null ? String(current.year) : '2026-2027';
    const label = `${league.name}${league.country ? ` (${league.country})` : ''}`;
    setForm((prev) => ({
      ...prev,
      externalLeagueId: String(league.id),
      externalSeason: seasonYear || prev.externalSeason,
      selectedLeagueLabel: label,
      leagueSearch: '',
      nameEn: prev.nameEn.trim() ? prev.nameEn : league.name || prev.nameEn,
      syncEnabled: true,
      seasonOptions: (league.seasons || []).map((s) => String(s.year))
    }));
    setLeagueOptions([]);
  };

  const clearLeagueLink = () => {
    setForm((prev) => ({
      ...prev,
      externalLeagueId: '',
      externalSeason: '',
      selectedLeagueLabel: '',
      syncEnabled: false,
      seasonOptions: []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameHe = form.nameHe.trim();
    const nameEn = form.nameEn.trim();
    const slug = form.slug.trim().toLowerCase();
    if (!nameHe) {
      toast.error(language === 'en' ? 'Hebrew name is required' : 'שם בעברית נדרש');
      return;
    }
    if (!nameEn) {
      toast.error(language === 'en' ? 'English name is required' : 'שם באנגלית נדרש');
      return;
    }
    if (!slug || !SLUG_OK.test(slug)) {
      toast.error(
        language === 'en'
          ? 'Slug: lowercase letters, numbers, hyphens only'
          : 'מזהה: אותיות קטנות, מספרים ומקפים בלבד'
      );
      return;
    }
    if (!form.startDate || !form.endDate) {
      toast.error(language === 'en' ? 'Start and end dates required' : 'נדרשים תאריך התחלה וסיום');
      return;
    }

    const linked = Boolean(form.externalLeagueId && form.externalSeason);
    const body = {
      nameHe,
      nameEn,
      slug,
      type: form.type,
      startDate: form.startDate,
      endDate: form.endDate,
      isActive: form.isActive,
      isDefault: form.isDefault,
      bettingLocked: form.bettingLocked,
      // Linked competitions always start with sync enabled (first import)
      syncEnabled: linked ? true : false
    };

    if (linked) {
      body.externalLeagueId = Number(form.externalLeagueId);
      body.externalSeason = String(form.externalSeason).trim();
    } else if (editingId) {
      body.clearExternalLink = true;
    }

    try {
      setSaving(true);
      let createdId = editingId;
      if (editingId) {
        await adminUpdateCompetition(editingId, body);
        toast.success(language === 'en' ? 'Competition updated' : 'התחרות עודכנה');
        await refreshCompetitions();
        setShowModal(false);

        if (linked && createdId) {
          try {
            setSyncingId(createdId);
            await adminSyncCompetition(createdId);
            toast.success(language === 'en' ? 'Synced from TheSportsDB' : 'סונכרן מ-TheSportsDB');
            navigate(`/admin/competitions/${createdId}/import-review`);
            return;
          } catch (syncErr) {
            errToast(syncErr);
            navigate(`/admin/competitions/${createdId}/import-review`);
          } finally {
            setSyncingId(null);
          }
        }
      } else {
        const data = await adminCreateCompetition(body);
        createdId = data?.competition?._id;
        toast.success(language === 'en' ? 'Competition created' : 'התחרות נוצרה');
        await refreshCompetitions();
        setShowModal(false);
        setCompetition(slug);

        if (linked && createdId) {
          if (data?.syncError) {
            toast.error(data.syncError);
          } else if (data?.syncSummary) {
            toast.success(language === 'en' ? 'Synced from TheSportsDB' : 'סונכרן מ-TheSportsDB');
          }
          navigate(`/admin/competitions/${createdId}/import-review`);
          return;
        }
      }
    } catch (err) {
      errToast(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async (c) => {
    try {
      setSyncingId(c._id);
      const data = await adminSyncCompetition(c._id);
      toast.success(language === 'en' ? 'Sync completed' : 'הסנכרון הושלם');
      await refreshCompetitions();
      if (data?.competition?.teamsImportStatus === 'pending_review') {
        navigate(`/admin/competitions/${c._id}/import-review`);
      }
    } catch (err) {
      errToast(err);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDelete = async (c) => {
    const dn = getCompetitionDisplayName(c, language);
    const ok =
      language === 'en'
        ? window.confirm(`Delete competition "${dn}"? Only allowed if it has no games.`)
        : window.confirm(`למחוק את התחרות "${dn}"? רק אם אין משחקים.`);
    if (!ok) return;
    try {
      await adminDeleteCompetition(c._id);
      toast.success(language === 'en' ? 'Competition deleted' : 'התחרות נמחקה');
      await refreshCompetitions();
    } catch (err) {
      errToast(err);
    }
  };

  const labelCls = `block font-semibold text-zinc-800 mb-2 ${isRTL ? 'text-right' : 'text-left'}`;
  const inputCls =
    'w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20';

  return (
    <AdminLayout
      title={t('manageCompetitions')}
      subtitle={t('competitionsAdminHelp')}
      actions={
        <Button variant="primary" size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          {t('addCompetition')}
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {competitions.map((c) => (
          <Card key={c._id} className="flex flex-col gap-3">
            {c.teamsImportStatus === 'pending_review' ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {language === 'en'
                  ? 'Teams awaiting import review.'
                  : 'קבוצות ממתינות לאישור ייבוא.'}{' '}
                <Link
                  className="font-semibold underline"
                  to={`/admin/competitions/${c._id}/import-review`}
                >
                  {language === 'en' ? 'Review now' : 'לאישור'}
                </Link>
              </div>
            ) : null}
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                  <Flag className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-bold text-zinc-900">
                    {getCompetitionDisplayName(c, language)}
                  </h3>
                  {getCompetitionAltName(c, language) ? (
                    <p className="truncate text-sm text-zinc-500">
                      {getCompetitionAltName(c, language)}
                    </p>
                  ) : null}
                  <p className="truncate text-sm text-zinc-500">
                    /c/{c.slug}
                    {c.isDefault ? (
                      <span className="ml-2 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                        {t('compDefault')}
                      </span>
                    ) : null}
                  </p>
                  {c.externalLeagueId != null ? (
                    <p className="mt-1 text-xs text-zinc-500">
                      TheSportsDB #{c.externalLeagueId}
                      {c.externalSeason != null ? ` · ${c.externalSeason}` : ''}
                      {c.lastSyncedAt
                        ? ` · ${language === 'en' ? 'synced' : 'סונכרן'} ${toInputDate(c.lastSyncedAt)}`
                        : ''}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap justify-end gap-1">
                {c.externalLeagueId != null ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleSync(c)}
                      loading={syncingId === c._id}
                      aria-label="Sync"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/admin/competitions/${c._id}/import-review`)}
                      aria-label="Import review"
                    >
                      <ClipboardCheck className="h-4 w-4" />
                    </Button>
                  </>
                ) : null}
                <Button variant="secondary" size="sm" onClick={() => openEdit(c)} aria-label={t('edit')}>
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(c)} aria-label={t('delete')}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-2 text-xs text-zinc-600">
              <div>
                <dt className="text-zinc-400">{t('compStart')}</dt>
                <dd>{toInputDate(c.startDate) || '—'}</dd>
              </div>
              <div>
                <dt className="text-zinc-400">{t('compEnd')}</dt>
                <dd>{toInputDate(c.endDate) || '—'}</dd>
              </div>
              <div>
                <dt className="text-zinc-400">{t('compActive')}</dt>
                <dd>{c.isActive ? (language === 'en' ? 'Yes' : 'כן') : language === 'en' ? 'No' : 'לא'}</dd>
              </div>
              <div>
                <dt className="text-zinc-400">{t('compAdminBettingLocked')}</dt>
                <dd>{c.bettingLocked ? (language === 'en' ? 'Yes' : 'כן') : language === 'en' ? 'No' : 'לא'}</dd>
              </div>
            </dl>
          </Card>
        ))}

        {competitions.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/80 py-16 text-center text-zinc-500">
            {language === 'en' ? 'No competitions loaded.' : 'לא נטענו תחרויות.'}
          </div>
        )}
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-zinc-900/50 p-4"
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-center text-xl font-bold text-zinc-900">
              {editingId
                ? language === 'en'
                  ? 'Edit competition'
                  : 'עריכת תחרות'
                : language === 'en'
                  ? 'New competition'
                  : 'תחרות חדשה'}
            </h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className={labelCls}>{t('competitionNameHe')}</label>
                <input
                  className={inputCls}
                  value={form.nameHe}
                  onChange={(e) => setForm({ ...form, nameHe: e.target.value })}
                  dir="rtl"
                  required
                />
              </div>
              <div>
                <label className={labelCls}>{t('competitionNameEn')}</label>
                <input
                  className={inputCls}
                  value={form.nameEn}
                  onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                  dir="ltr"
                  required
                />
              </div>
              <div>
                <label className={labelCls}>{t('compSlug')}</label>
                <input
                  className={inputCls}
                  value={form.slug}
                  onChange={(e) =>
                    setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })
                  }
                  required
                  placeholder="e.g. main, world-cup-2026"
                />
              </div>

              <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-3">
                <label className={labelCls}>
                  {language === 'en' ? 'TheSportsDB league' : 'ליגה מ-TheSportsDB'}
                </label>
                {form.selectedLeagueLabel || form.externalLeagueId ? (
                  <div className="mb-2 flex items-center justify-between gap-2 text-sm text-zinc-700">
                    <span>
                      {form.selectedLeagueLabel || `League #${form.externalLeagueId}`}
                      {form.externalSeason ? ` · ${form.externalSeason}` : ''}
                    </span>
                    <button
                      type="button"
                      className="text-xs font-semibold text-red-600"
                      onClick={clearLeagueLink}
                    >
                      {language === 'en' ? 'Clear' : 'נקה'}
                    </button>
                  </div>
                ) : null}
                <input
                  className={inputCls}
                  value={form.leagueSearch}
                  onChange={(e) => setForm({ ...form, leagueSearch: e.target.value })}
                  placeholder={
                    language === 'en'
                      ? 'Search (e.g. Israel, Ligat, World Cup)'
                      : 'חיפוש (למשל Israel, Ligat, World Cup)'
                  }
                  dir="ltr"
                />
                {leagueSearching ? (
                  <p className="mt-1 text-xs text-zinc-500">
                    {language === 'en' ? 'Searching…' : 'מחפש…'}
                  </p>
                ) : null}
                {leagueOptions.length > 0 ? (
                  <ul className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-zinc-200 bg-white text-sm">
                    {leagueOptions.map((league) => (
                      <li key={league.id}>
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                          onClick={() => selectLeague(league)}
                        >
                          {league.logo ? (
                            <img src={league.logo} alt="" className="h-5 w-5 object-contain" />
                          ) : null}
                          <span className="min-w-0 truncate">
                            {league.name}
                            {league.country ? ` · ${league.country}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {form.externalLeagueId ? (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>
                        {language === 'en' ? 'Season' : 'עונה'}
                      </label>
                      <input
                        className={inputCls}
                        dir="ltr"
                        value={form.externalSeason}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            externalSeason: e.target.value.replace(/[^\d-]/g, '')
                          })
                        }
                        placeholder="2026-2027"
                        list="sportsdb-season-suggestions"
                      />
                      <datalist id="sportsdb-season-suggestions">
                        {(form.seasonOptions || []).map((s) => (
                          <option key={s} value={s} />
                        ))}
                      </datalist>
                      <p className="mt-1 text-xs text-zinc-500">
                        {language === 'en'
                          ? 'Format YYYY-YYYY (e.g. 2026-2027 for Ligat Ha\'Al).'
                          : 'פורמט YYYY-YYYY (למשל 2026-2027 לליגת העל).'}
                      </p>
                    </div>
                    <label className="mt-6 flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-800">
                      <input
                        type="checkbox"
                        checked={form.syncEnabled}
                        onChange={(e) => setForm({ ...form, syncEnabled: e.target.checked })}
                        className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                      />
                      {language === 'en' ? 'Auto sync' : 'סנכרון אוטומטי'}
                    </label>
                  </div>
                ) : null}
              </div>

              <div>
                <label className={labelCls}>{t('compType')}</label>
                <select
                  className={inputCls}
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                >
                  <option value="league">{t('compLeague')}</option>
                  <option value="other">{t('compOther')}</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t('compStart')}</label>
                  <DateInputDMY
                    className={inputCls}
                    value={form.startDate}
                    onChange={(iso) => setForm({ ...form, startDate: iso })}
                    language={language}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('compEnd')}</label>
                  <DateInputDMY
                    className={inputCls}
                    value={form.endDate}
                    onChange={(iso) => setForm({ ...form, endDate: iso })}
                    language={language}
                    required
                  />
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-800">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                />
                {t('compActive')}
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-800">
                <input
                  type="checkbox"
                  checked={form.isDefault}
                  onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                  className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                />
                {t('compDefault')}
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-800">
                <input
                  type="checkbox"
                  checked={form.bettingLocked}
                  onChange={(e) => setForm({ ...form, bettingLocked: e.target.checked })}
                  className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                />
                {t('compAdminBettingLocked')}
              </label>
              <div className="mt-2 flex gap-2">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowModal(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" loading={saving}>
                  {t('save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCompetitions;
